import { FC } from 'react';

import { useParams } from 'react-router';

import { Breadcrumbs, Container, Stack, Typography } from '@mui/material';

import SlashForwardIcon from '@/components/Icons/SlashForwardIcon';
import Meta from '@/components/Meta';
import useIsTeamsEnabled from '@/hooks/useIsTeamsEnabled';
import GlobalHeader from '@/sections/GlobalHeader';
import Sidebar from '@/sections/Sidebar';
import TeamMenu from '@/sections/Teams/TeamMenu';
import asyncComponentLoader from '@/utils/react/loader';
import { DialogsProvider } from '@toolpad/core/useDialogs';

import Header from './Header';
import { MatchParams, Tabs } from './types';

const ContentComponents: Record<Tabs, FC> = {
  [Tabs.General]: asyncComponentLoader(() => import('./General')),
  [Tabs.Members]: asyncComponentLoader(() => import('./Members')),
  [Tabs.Environments]: asyncComponentLoader(() => import('./Environments')),
  [Tabs.Secrets]: asyncComponentLoader(() => import('@/pages/Settings/Tabs/Secrets')),
};

function TeamSettings() {
  const isTeamsEnabled = useIsTeamsEnabled();
  const { tabId } = useParams<MatchParams>();

  if (!isTeamsEnabled) return null;

  function getContent() {
    const ContentComponent = ContentComponents[tabId || Tabs.General];

    return (
      <Stack
        sx={{
          overflow: 'auto',
          height: '100%',
          backgroundColor: (theme) => theme.palette.background.subtle,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
        key={tabId}
      >
        <Stack
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            position: 'relative',
            flexGrow: 1,
          }}
        >
          <Container maxWidth="lg" sx={{ py: 4, height: '100%' }}>
            <ContentComponent />
          </Container>
        </Stack>
      </Stack>
    );
  }

  return (
    <Meta title="Team settings">
      <DialogsProvider>
        <Stack direction="row" height="100%" width="100%" overflow="hidden">
          <Sidebar />
          <Stack
            sx={{
              height: '100%',
              width: '100%',
              overflow: 'auto',
            }}
          >
            <GlobalHeader
              title={
                <Breadcrumbs
                  separator={
                    <SlashForwardIcon sx={{ color: (theme) => theme.palette.secondary.light }} />
                  }
                >
                  <TeamMenu />
                  <Typography variant="h5" color="text.default">
                    Manage team
                  </Typography>
                </Breadcrumbs>
              }
              sx={{
                pl: 4,
              }}
            />
            <Header />
            {getContent()}
          </Stack>
        </Stack>
      </DialogsProvider>
    </Meta>
  );
}

export default TeamSettings;
